import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  Users,
  HelpCircle,
  Layers,
  LogOut,
  Brain,
} from 'lucide-react'
import { Container } from '../layout/Container'
import { Button } from '../ui/Button'
import type { AuthUser } from '../../types'

interface AdminLayoutProps {
  user: AuthUser | null
  onLogout: () => void
  children?: React.ReactNode
}

export function AdminLayout({ user, onLogout, children }: AdminLayoutProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const navItems = [
    { to: '/admin', label: t('admin.nav.dashboard'), icon: LayoutDashboard, end: true },
    { to: '/admin/leads', label: t('admin.nav.leads'), icon: Users },
    { to: '/admin/faqs', label: t('admin.nav.faqs'), icon: HelpCircle },
    { to: '/admin/areas', label: t('admin.nav.areas'), icon: Layers },
    { to: '/app/ingest', label: 'Base de Conocimientos', icon: Brain, external: true },
  ]

  const handleLogout = () => {
    onLogout()
    navigate('/admin')
  }

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
      isActive
        ? 'bg-accent/10 text-accent-light'
        : 'text-text-secondary hover:bg-surface hover:text-text-primary'
    }`

  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-border bg-surface">
        <Container size="wide">
          <div className="flex h-16 items-center justify-between">
            <Link to="/" className="text-xl font-semibold text-gradient">
              EmpleaHoy IA
            </Link>
            <div className="flex items-center gap-4">
              <span className="hidden text-sm text-text-secondary md:inline">
                {user?.email}
              </span>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                {t('admin.logout')}
              </Button>
            </div>
          </div>
        </Container>
      </header>

      <div className="flex flex-1">
        <aside className="hidden w-64 border-r border-border bg-surface-elevated lg:block">
          <nav className="p-4">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.to}>
                  {item.external ? (
                    <Link
                      to={item.to}
                      className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-text-secondary transition-colors hover:bg-surface hover:text-text-primary"
                    >
                      <item.icon className="h-5 w-5" />
                      {item.label}
                    </Link>
                  ) : (
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={navLinkClass}
                    >
                      <item.icon className="h-5 w-5" />
                      {item.label}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <main className="flex-1 p-4 md:p-8">
          <Container size="wide">
            {children}
          </Container>
        </main>
      </div>
    </div>
  )
}
